<template>
  <div class="payment-step-20676">
    <div
      class="payment-step-20676__stripe-card"
      :class="{ 'payment-step-20676__stripe-card--ready': isStripeReady }"
    >
      <StripeCard
        ref="stripeCard"
        :billing="billing"
        :show-button="false"
        @elementsReady="isStripeReady = true"
        @loadError="hasStripeLoadError = true"
        @saved="onCardSaved"
      >
        <template v-if="isStripeReady" #actions>
          <v-wait for="Subscribing">
            <template v-slot:waiting>
              <span
                class="payment-step-20676__submit btns btns--large"
                disabled
              >
                Processing... <button-spinner />
              </span>
            </template>
            <button
              type="button"
              class="payment-step-20676__submit btns btns--large"
              @click="startFreeTrial"
            >
              Start Free Trial
            </button>
          </v-wait>
        </template>
      </StripeCard>
    </div>

    <p v-if="hasStripeLoadError" class="payment-step-20676__load-error">
      We couldn't load the payment form. Please try again later.
    </p>

    <!--
      Always available, regardless of whether Stripe ever loads -- Stripe's
      own <script> can fail entirely (e.g. blocked by an ad/privacy blocker),
      in which case the payment form above never renders at all and this is
      the only way to continue the onboarding.
      https://github.com/shortformhq/main/issues/20676
    -->
    <button
      type="button"
      class="payment-step-20676__skip"
      :disabled="wait.is('Subscribing')"
      @click="onSkip"
    >
      Skip for now
    </button>

    <FormError :errors="validation.errors" :field="plan.id" />

    <p class="payment-step-20676__disclosure">
      By clicking the "Start Free Trial" button above, you agree to our
      <router-link target="_blank" :to="{ name: 'site_terms' }"
        >Terms of Use</router-link
      >
      and
      <router-link target="_blank" :to="{ name: 'site_privacy' }"
        >Privacy Policy</router-link
      >.
      <span v-if="hasTrialDays"
        >You may cancel any time in your free trial and you will not be charged. </span
      >To cancel, go to Billing and click on "Cancel subscription." <br /><br />
      <strong v-if="hasTrialDays">
        Shortform will automatically continue your subscription at the end of
        your free trial on {{ trialEnd }} and {{ subscribePopupChargesText }}
      </strong>
      <strong v-else>
        Shortform will automatically continue your subscription and
        {{ subscribePopupChargesText }}
      </strong>
      There are no refunds or credits for partial subscription periods.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useWait } from '@/app/composables/useWait'
import type { BillingView, PlanView } from '@/models/billing'
import { ValidationState } from '@/helpers/form'
import { Mixpanel } from '@/analytics/mixpanel'

import StripeCard from '@/app/components/billing/StripeCard.vue'
import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'

const props = defineProps<{
  billing: BillingView
  plan: PlanView
}>()

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'skip'): void
}>()

const wait = useWait()
const stripeCard = ref<InstanceType<typeof StripeCard> | null>(null)
const validation = ref(new ValidationState())
const isStripeReady = ref(false)
// `elementsReady` never fires after a Stripe load error (network issue, ad
// blocker, etc.) -- without this, Skip would stay hidden forever alongside
// a payment form that can never work. See #20357 review discussion.
const hasStripeLoadError = ref(false)

const hasTrialDays = computed(() => true)

const trialEnd = computed(() => {
  return props.billing.trialEnd(props.plan.id)
})

const subscribePopupChargesText = computed(() => {
  if (props.plan.nickname === 'Annual' && props.plan.discount) {
    return (
      `charge the subscription fee of ${props.plan.price.perPeriod} to your payment ` +
      `method for your first ${props.plan.labels.period}, ` +
      `then ${props.plan.priceNoDiscount.perPeriod} ` +
      `for subsequent ${props.plan.labels.period}s on ${props.plan.labels.basis} basis until you cancel.`
    )
  }

  return (
    `charge the subscription fee of ${props.plan.price.perPeriod} ` +
    `to your payment method on ${props.plan.labels.basis} basis until you cancel.`
  )
})

async function startFreeTrial(): Promise<void> {
  try {
    let cardSuccess = true
    wait.start('Subscribing')
    if (props.billing.cardEditMode && stripeCard.value?.needsSaving()) {
      cardSuccess = await stripeCard.value.save()
    }
    if (cardSuccess) {
      await subscribe()
    }
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    validation.value.showErrors(error)
  }
}

/**
 * StripeCard's Express Checkout element (Apple Pay/Google Pay) saves the
 * card and emits `saved` on its own, outside the "Start Free Trial" button
 * flow.
 *
 * We check if the wait state is already active, and if so, we don't call `subscribe()`
 * again, since that would be a duplicate subscription attempt.
 */
async function onCardSaved(): Promise<void> {
  Mixpanel.trackOnboardingStartFreeTrial(props.plan.id)

  if (wait.is('Subscribing')) {
    return
  }

  try {
    wait.start('Subscribing')
    await subscribe()
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    validation.value.showErrors(error)
  }
}

async function subscribe(): Promise<void> {
  await props.billing.subscribeTo(props.plan)
  emit('saved')
  validation.value.reset()
}

/**
 * The `disabled` attribute above should already stop this, but that relies
 * on the DOM re-rendering in time -- guard here too so a user can't skip out
 * mid-payment via a duplicate/fast click.
 */
function onSkip(): void {
  if (wait.is('Subscribing')) {
    return
  }

  emit('skip')
}
</script>

<style scoped lang="scss">
.payment-step-20676 {
  &__stripe-card--ready {
    :deep(#express-checkout-element .StripeElement) {
      background: $c-white;
      border: 1px solid $c-border-4;
      border-radius: 10px;
      padding: 20px;
    }

    :deep(.stripe-payment-element__card-box) {
      background: $c-white;
      border: 1px solid $c-border-4;
      border-radius: 10px;
      padding: 20px;
    }
  }

  &__submit {
    width: 100%;
    background-color: $c-blue-4;
    border-color: $c-blue-4;
    color: $c-white;

    @include hover {
      background-color: $c-blue-4;
      border-color: $c-blue-4;
      color: $c-white;
      opacity: 0.9;
    }
  }

  &__skip {
    display: block;
    margin: 16px auto 0;
    font-size: 14px;
    color: $c-black-3;
    text-decoration: underline;
  }

  &__load-error {
    font-size: 14px;
    line-height: 20px;
    color: $c-black-3;
    text-align: center;
  }

  &__disclosure {
    margin-top: 20px;
    font-size: 11px;
    line-height: 16px;
    color: $c-black-3;
  }
}
</style>
